import { normalizeQuotes, usesCurlyQuotes } from "../ai/normalizeQuotes";

const LEFT_DOUBLE_CURLY = "\u201c";
const RIGHT_DOUBLE_CURLY = "\u201d";

const OPENING_DOUBLE_QUOTES = new Set(['"', LEFT_DOUBLE_CURLY]);
const CLOSING_DOUBLE_QUOTES = new Set(['"', RIGHT_DOUBLE_CURLY]);

function firstNonWhitespaceChar(text: string): string {
  const match = text.match(/\S/u);
  return match?.[0] ?? "";
}

function lastNonWhitespaceChar(text: string): string {
  const trimmed = text.replace(/\s+$/u, "");
  return trimmed[trimmed.length - 1] ?? "";
}

function isOpeningDoubleQuote(char: string): boolean {
  return OPENING_DOUBLE_QUOTES.has(char);
}

function isClosingDoubleQuote(char: string): boolean {
  return CLOSING_DOUBLE_QUOTES.has(char);
}

const countDoubleQuotes = (text: string) => {
  const curlyOpenings = (text.match(/“/g) || []).length;
  const curlyClosings = (text.match(/”/g) || []).length;
  const straightQuotes = (text.match(/\x22/g) || []).length;
  return { curlyOpenings, curlyClosings, straightQuotes };
};

function hasUnmatchedClosingQuote(text: string): boolean {
  const { curlyOpenings, curlyClosings, straightQuotes } = countDoubleQuotes(text);
  return (curlyClosings > curlyOpenings) || (straightQuotes % 2 === 1);
}

const hasBalancedDialogue = (text: string): boolean => {
  const { curlyOpenings, curlyClosings, straightQuotes } = countDoubleQuotes(text);
  if (curlyOpenings !== curlyClosings || straightQuotes % 2 !== 0 || curlyOpenings + straightQuotes === 0) return false;

  // Equal totals can still hide missing outer framing: Huh,” she said. “Again.
  let curlyDepth = 0;
  let straightOpen = false;
  for (const match of text.matchAll(/[“”"]/g)) {
    if (match[0] === '"') {
      const before = text[match.index - 1] ?? "";
      const after = text[match.index + 1] ?? "";
      // An opening straight quote needs an opening boundary and quoted content.
      // A quote after a word/comma followed by whitespace is a closing quote.
      if (!straightOpen && ((before && !/[\s([{—–:]/u.test(before)) || !/\S/u.test(after))) return false;
      straightOpen = !straightOpen;
    } else {
      curlyDepth += match[0] === LEFT_DOUBLE_CURLY ? 1 : -1;
      if (curlyDepth < 0) return false;
    }
  }
  return true;
};

function pickDoubleQuotePair(styleReference: string): { open: string; close: string } {
  if (usesCurlyQuotes(styleReference)) {
    return { open: LEFT_DOUBLE_CURLY, close: RIGHT_DOUBLE_CURLY };
  }
  return { open: '"', close: '"' };
}

function stripLeadingOpeningDoubleQuote(text: string): string {
  const leadingWhitespace = text.match(/^\s*/u)?.[0] ?? "";
  const rest = text.slice(leadingWhitespace.length);
  const first = rest[0] ?? "";
  if (!isOpeningDoubleQuote(first)) return text;
  return `${leadingWhitespace}${rest.slice(1)}`;
}

function stripTrailingClosingDoubleQuote(text: string): string {
  const trailingWhitespace = text.match(/\s*$/u)?.[0] ?? "";
  const core = text.slice(0, text.length - trailingWhitespace.length);
  const last = core[core.length - 1] ?? "";
  if (!isClosingDoubleQuote(last)) return text;
  return `${core.slice(0, -1)}${trailingWhitespace}`;
}

function curlyDoubleQuotesToStraight(text: string): string {
  return text.replace(/\u201c/g, '"').replace(/\u201d/g, '"');
}

function manuscriptUsesStraightDoubleQuotes(styleReference: string): boolean {
  return /"/.test(styleReference) && !usesCurlyQuotes(styleReference);
}

function reconcileReplacementQuoteFraming(
  newText: string,
  replaced: string,
  left: string,
  right: string,
  replacementHasBalancedDialogue: boolean,
): string {
  const styleReference = `${left.slice(-80)}${replaced}${right.slice(0, 80)}`;
  const { open, close } = pickDoubleQuotePair(styleReference);

  let text = newText;
  const leftLast = lastNonWhitespaceChar(left);
  const rightFirst = firstNonWhitespaceChar(right);
  const replacedFirst = firstNonWhitespaceChar(replaced);
  const replacedLast = lastNonWhitespaceChar(replaced);

  let textFirst = firstNonWhitespaceChar(text);
  let textLast = lastNonWhitespaceChar(text);

  const leftHasOpening = isOpeningDoubleQuote(leftLast);
  const rightHasClosing = isClosingDoubleQuote(rightFirst);
  const replacedHadOpening = isOpeningDoubleQuote(replacedFirst);
  const replacedHadClosing = isClosingDoubleQuote(replacedLast);
  let textHasOpening = isOpeningDoubleQuote(textFirst);
  let textHasClosing = isClosingDoubleQuote(textLast);

  // A complete rewrite can move narration outside dialogue. Classify before
  // context deduplication removes any quotes from the replacement.
  const preservesOwnDialogueFraming = replacedHadOpening
    && hasBalancedDialogue(replaced)
    && !leftHasOpening
    && !rightHasClosing
    && !textHasOpening
    && replacementHasBalancedDialogue;

  if (textHasOpening && leftHasOpening) {
    text = stripLeadingOpeningDoubleQuote(text);
    textFirst = firstNonWhitespaceChar(text);
    textHasOpening = isOpeningDoubleQuote(textFirst);
  }

  if (textHasClosing && rightHasClosing) {
    text = stripTrailingClosingDoubleQuote(text);
    textLast = lastNonWhitespaceChar(text);
    textHasClosing = isClosingDoubleQuote(textLast);
  }

  const partialDialogueSelection = replacedHadClosing && !replacedHadOpening && hasUnmatchedClosingQuote(replaced);
  if (
    (partialDialogueSelection || replacedHadOpening)
    && !preservesOwnDialogueFraming
    && !leftHasOpening
    && !textHasOpening
  ) {
    text = `${open}${text}`;
    textHasOpening = true;
  }

  if (
    (replacedHadClosing || partialDialogueSelection)
    && !preservesOwnDialogueFraming
    && !rightHasClosing
    && !textHasClosing
  ) {
    text = `${text}${close}`;
  }

  return text;
}

/**
 * Match generated replacement prose to the manuscript's quote style and preserve
 * dialogue framing when the replaced span omitted opening/closing quotes.
 */
export function adaptReplacementQuotes(params: {
  newText: string;
  replaced: string;
  left?: string;
  right?: string;
}): string {
  const left = params.left ?? "";
  const right = params.right ?? "";
  const replaced = params.replaced ?? "";
  const styleReference = `${left.slice(-80)}${replaced}${right.slice(0, 80)}`;

  // Keep directional/boundary evidence before normalization changes quote glyphs.
  const replacementHasBalancedDialogue = hasBalancedDialogue(params.newText);
  let adjusted = normalizeQuotes(styleReference, params.newText);
  if (manuscriptUsesStraightDoubleQuotes(styleReference) && /[\u201c\u201d\u2018\u2019]/u.test(adjusted)) {
    adjusted = curlyDoubleQuotesToStraight(adjusted);
  }

  return reconcileReplacementQuoteFraming(adjusted, replaced, left, right, replacementHasBalancedDialogue);
}
